/**
 * Evaluates an IR directly against an in-memory N3 store — no SPARQL engine, no
 * network. Our IR is small (BGP + OPTIONAL + comparison FILTER + ORDER/LIMIT +
 * COUNT/ASK), so a nested-loop join is more than enough and keeps the browser
 * bundle tiny. This is the local-source counterpart to the fetch executor;
 * Comunica remains the drop-in upgrade for heavier SPARQL/federation.
 */
import { DataFactory, type Store, type Term as N3Term } from "n3";
import type { Filter, QueryIR, Term, TriplePattern } from "../ir/types.ts";
import type {
  ExecuteOptions,
  QueryExecutor,
  SparqlBinding,
  SparqlResults,
} from "./types.ts";
import { RDFS_LABEL } from "../rdf/ontology.ts";

const { namedNode, literal } = DataFactory;
const SKOS_PREF_LABEL = "http://www.w3.org/2004/02/skos/core#prefLabel";
const XSD_STRING = "http://www.w3.org/2001/XMLSchema#string";

type Binding = Map<string, N3Term>;

export class LocalStoreEvaluator implements QueryExecutor {
  private readonly store: Store;
  constructor(store: Store) {
    this.store = store;
  }

  async run(_sparql: string, _source: unknown, opts?: ExecuteOptions): Promise<SparqlResults> {
    const started = Date.now();
    const ir = opts?.ir;
    if (!ir) throw new Error("LocalStoreEvaluator requires the IR (pass opts.ir).");
    const result = this.evaluate(ir);
    result.elapsedMs = Date.now() - started;
    return result;
  }

  private matchTerm(t: Term, binding: Binding): N3Term | null {
    if (t.kind === "var") return binding.get(t.name) ?? null;
    if (t.kind === "iri") return namedNode(t.value);
    if (t.language) return literal(t.value, t.language);
    if (t.datatype) return literal(t.value, namedNode(t.datatype));
    return literal(t.value);
  }

  private bind(b: Binding, t: Term, value: N3Term): boolean {
    if (t.kind !== "var") return true;
    const existing = b.get(t.name);
    if (existing) return existing.equals(value);
    b.set(t.name, value);
    return true;
  }

  /** Extend each incoming binding by matching one triple pattern. */
  private matchPattern(pattern: TriplePattern, seeds: Binding[]): Binding[] {
    const out: Binding[] = [];
    for (const seed of seeds) {
      const s = this.matchTerm(pattern.subject, seed);
      const p = this.matchTerm(pattern.predicate, seed);
      const o = this.matchTerm(pattern.object, seed);
      // A var already bound resolves to its term; an unbound var → null wildcard.
      const s2 = pattern.subject.kind === "var" && !s ? null : s;
      const p2 = pattern.predicate.kind === "var" && !p ? null : p;
      const o2 = pattern.object.kind === "var" && !o ? null : o;
      for (const q of this.store.getQuads(s2, p2, o2, null)) {
        const b: Binding = new Map(seed);
        if (
          this.bind(b, pattern.subject, q.subject) &&
          this.bind(b, pattern.predicate, q.predicate) &&
          this.bind(b, pattern.object, q.object)
        ) {
          out.push(b);
        }
      }
    }
    return out;
  }

  private joinBgp(patterns: TriplePattern[]): Binding[] {
    let solutions: Binding[] = [new Map()];
    for (const pattern of patterns) solutions = this.matchPattern(pattern, solutions);
    return solutions;
  }

  private leftJoin(solutions: Binding[], pattern: TriplePattern): Binding[] {
    const out: Binding[] = [];
    for (const sol of solutions) {
      const matches = this.matchPattern(pattern, [sol]);
      if (matches.length) out.push(...matches);
      else out.push(sol);
    }
    return out;
  }

  private applyFilter(solutions: Binding[], f: Filter): Binding[] {
    return solutions.filter((b) => {
      const term = b.get(f.variable);
      if (!term) return false;
      const left = term.value;
      const right = f.value.value;
      const ln = Number(left);
      const rn = Number(right);
      const numeric = left !== "" && right !== "" && !Number.isNaN(ln) && !Number.isNaN(rn);
      const [a, c] = numeric ? [ln, rn] : [left, right];
      switch (f.op) {
        case ">": return a > c;
        case "<": return a < c;
        case ">=": return a >= c;
        case "<=": return a <= c;
        case "=": return a === c;
        case "!=": return a !== c;
      }
    });
  }

  private labelOf(iri: string): string | undefined {
    const lits = [
      ...this.store.getObjects(namedNode(iri), namedNode(RDFS_LABEL), null),
      ...this.store.getObjects(namedNode(iri), namedNode(SKOS_PREF_LABEL), null),
    ].filter((o) => o.termType === "Literal");
    const preferred = lits.find((o) => o.language === "en" || o.language === "");
    return (preferred ?? lits[0])?.value;
  }

  private toBinding(term: N3Term): SparqlBinding {
    if (term.termType === "NamedNode") return { type: "uri", value: term.value };
    if (term.termType === "BlankNode") return { type: "bnode", value: term.value };
    const out: SparqlBinding = { type: "literal", value: term.value };
    if (term.termType === "Literal") {
      if (term.language) out["xml:lang"] = term.language;
      else if (term.datatype && term.datatype.value !== XSD_STRING) out.datatype = term.datatype.value;
    }
    return out;
  }

  private evaluate(ir: QueryIR): SparqlResults {
    const required = ir.patterns.filter((p) => p.optional !== true);
    const optional = ir.patterns.filter((p) => p.optional === true);

    let solutions = this.joinBgp(required);
    for (const opt of optional) solutions = this.leftJoin(solutions, opt);
    for (const f of ir.filters) solutions = this.applyFilter(solutions, f);

    if (ir.form === "ask") {
      return { head: [], rows: [], boolean: solutions.length > 0, elapsedMs: 0 };
    }

    if (ir.form === "count") {
      const countVar = ir.projection[0] ?? ir.focusVar;
      const distinct = new Set<string>();
      for (const b of solutions) {
        const t = b.get(countVar);
        if (t) distinct.add(t.value);
      }
      return {
        head: ["count"],
        rows: [{ count: { type: "literal", value: String(distinct.size) } }],
        elapsedMs: 0,
      };
    }

    // SELECT — distinct → order → limit.
    if (ir.distinct) {
      const seen = new Set<string>();
      solutions = solutions.filter((b) => {
        const key = ir.projection.map((v) => b.get(v)?.value ?? "").join("");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    if (ir.orderBy) {
      const { variable, descending } = ir.orderBy;
      solutions.sort((a, b) => {
        const av = a.get(variable)?.value ?? "";
        const bv = b.get(variable)?.value ?? "";
        const an = Number(av);
        const bn = Number(bv);
        const cmp =
          !Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && bv !== ""
            ? an - bn
            : av < bv ? -1 : av > bv ? 1 : 0;
        return descending ? -cmp : cmp;
      });
    }
    const limit = ir.limit ?? 200;
    solutions = solutions.slice(0, limit);

    const head: string[] = [];
    for (const v of ir.projection) {
      head.push(v);
      if (ir.wantLabels && v !== ir.orderBy?.variable) head.push(`${v}Label`);
    }

    const rows = solutions.map((b) => {
      const row: Record<string, SparqlBinding> = {};
      for (const v of ir.projection) {
        const term = b.get(v);
        if (term) row[v] = this.toBinding(term);
        if (ir.wantLabels && v !== ir.orderBy?.variable && term?.termType === "NamedNode") {
          const label = this.labelOf(term.value);
          if (label !== undefined) row[`${v}Label`] = { type: "literal", value: label };
        }
      }
      return row;
    });

    return { head, rows, elapsedMs: 0 };
  }
}
